import { useMemo, useRef, useState } from 'react';
import { useAsyncFn } from 'react-use';

import {
  AdornedInput,
  Anchor,
  Box,
  Button,
  Combobox,
  Form,
  FormActions,
  FormField,
  FormHelp,
  Icon,
  Input,
  Label,
  LoadingMask,
  Select,
  Textarea,
} from '@zillow/constellation-10';
import { IconHammerFilled } from '@zillow/constellation-icons';

import { PartialMessage } from '@zg-rentals/backend-styled-components';
import { stringHelper } from '@zg-rentals/rental-manager-env';
import {
  RentalManagerPropertyAddressVerificationWebApiHandler_verification,
  RentalManagerSupportHelpCategory,
} from '@zg-rentals/ts-rental-manager-api-client';
import { CharacterCountFormHelp } from '@zg-rentals/visual-cohesion';

import { useAddressAutocomplete } from '../../hooks/queries/address';
import { useGetHelpTopics } from '../../hooks/queries/support';
import { isUnitNumberOverLimit } from '../../utils/propertyValidationUtils';

import AddressConstants from '../../constants/AddressConstants';

import type { ChangeEvent, FormEvent } from 'react';
import type { ComboboxOptionType } from '@zillow/constellation-10';
import type {
  GuestSupportGetHelpTopicsCategoryTopicWebDto,
  GuestSupportGetHelpTopicsCategoryWebDto,
  GuestSupportSubmitRequestWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  onSubmit: (params: GuestSupportSubmitRequestWebDto) => void;
  queryListingAlias?: string;
  queryListingAddress?: string;
  queryUnit?: string;
  isCategoryReadOnly: boolean;
  isTopicReadOnly: boolean;
  isSubmitting: boolean;
  selectedHelpCategory: GuestSupportGetHelpTopicsCategoryWebDto;
  selectedHelpTopic: GuestSupportGetHelpTopicsCategoryTopicWebDto;
  isLoggedIn: boolean;
  userFirstName?: string;
  userLastName?: string;
  userEmail?: string;
  signInUrl: string;
  onCategorySelect: (category: GuestSupportGetHelpTopicsCategoryWebDto) => void;
  onTopicSelect: (topic: GuestSupportGetHelpTopicsCategoryTopicWebDto) => void;
};

const SubmitRequestForm = ({
  onSubmit,
  isCategoryReadOnly,
  isTopicReadOnly,
  queryListingAlias,
  queryListingAddress,
  queryUnit,
  isSubmitting,
  selectedHelpCategory,
  selectedHelpTopic,
  isLoggedIn,
  userFirstName = '',
  userLastName = '',
  userEmail = '',
  signInUrl,
  onCategorySelect,
  onTopicSelect,
}: Props) => {
  const [listingAddress, setListingAddress] = useState(queryListingAddress || '');
  const [unit, setUnit] = useState(queryUnit || '');
  const [email, setEmail] = useState(userEmail);
  const [isEmailError, setIsEmailError] = useState(false);
  const [description, setDescription] = useState('');
  const [firstName, setFirstName] = useState(userFirstName);
  const [lastName, setLastName] = useState(userLastName);
  const [isUnitFocused, setIsUnitFocused] = useState(false);
  const [setAddress, queryData] = useAddressAutocomplete();
  const addressInputRef = useRef<HTMLInputElement>(null);

  const isUnitOverLimit = isUnitNumberOverLimit(unit);

  const { data: helpTopics } = useGetHelpTopics({ isLoggedIn });

  const [addressVerificationState, verifyAddress] = useAsyncFn(async (address: string) => {
    if (address === '') {
      return;
    }

    return RentalManagerPropertyAddressVerificationWebApiHandler_verification({
      fullAddress: address,
    });
  });

  const isLoginAndAddressRequired = selectedHelpCategory?.key === RentalManagerSupportHelpCategory.MY_LISTING;
  const canSubmit = useMemo<boolean>(() => {
    if (isEmailError) return false;
    if (addressVerificationState.error) return false;
    if (isUnitOverLimit) return false;
    if (!isLoggedIn && !stringHelper.validateEmail(email)) return false;
    if (isLoginAndAddressRequired && (listingAddress === '' || !isLoggedIn)) return false;
    return (
      description !== '' && (selectedHelpCategory?.key as string) !== '' && (selectedHelpTopic?.key as string) !== ''
    );
  }, [
    isEmailError,
    addressVerificationState.error,
    isUnitOverLimit,
    isLoggedIn,
    email,
    isLoginAndAddressRequired,
    listingAddress,
    description,
    selectedHelpCategory?.key,
    selectedHelpTopic?.key,
  ]);

  const verifyAndUpdateEmail = (newEmail: string) => {
    setIsEmailError(newEmail !== '' ? !stringHelper.validateEmail(newEmail) : false);
    setEmail(newEmail);
  };

  const handleSelectAddress = async (option: ComboboxOptionType) => {
    const addressValue = typeof option === 'string' ? option : option.value;

    await verifyAddress(addressValue);
    setListingAddress(addressValue);
  };

  const handleAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
    setListingAddress(e.target.value);

    setAddress(e.target.value);
  };

  const handleSelectCategory = (value: string) => {
    const selectedCategory = helpTopics?.categories?.find((category) => (category.key as string) === value);
    if (selectedCategory) {
      onCategorySelect(selectedCategory);
    }
  };

  const handleSelectTopic = (value: string) => {
    const selectedTopic = selectedHelpCategory?.topics?.find(
      (topic: GuestSupportGetHelpTopicsCategoryTopicWebDto) => (topic.key as string) === value,
    );
    if (selectedTopic) {
      onTopicSelect(selectedTopic);
    }
  };

  const handleSubmitRequest = () => {
    const params: GuestSupportSubmitRequestWebDto = {
      category: selectedHelpCategory?.key,
      topic: selectedHelpTopic?.key,
      description,
      email,
      firstName,
      lastName,
      listingAddress,
      unit,
      ...(queryListingAlias ? { listingAlias: queryListingAlias } : {}),
    };
    onSubmit(params);
  };

  return (
    <Form asChild={true} data-testid="SubmitRequestForm" onSubmit={(e: FormEvent) => e.preventDefault()}>
      <div>
        <LoadingMask css={{ display: 'block' }} loading={isSubmitting}>
          <FormField
            label={<Label>{isCategoryReadOnly ? 'Category' : 'I need help with'}</Label>}
            control={
              isCategoryReadOnly ? (
                <Input value={selectedHelpCategory?.description} readOnly={true} />
              ) : (
                <Select
                  aria-label="RequestType"
                  name="requestType"
                  value={selectedHelpCategory?.key}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => handleSelectCategory(e.target.value)}
                  css={{ whiteSpace: 'pre' }}
                >
                  <option disabled={(selectedHelpCategory?.key as string) !== ''} aria-label="Select an option">
                    -
                  </option>

                  {helpTopics?.categories?.map((category: GuestSupportGetHelpTopicsCategoryWebDto) => {
                    return (
                      <option key={category?.key} value={category?.key} aria-label={category?.description}>
                        {category?.description}
                      </option>
                    );
                  })}
                </Select>
              )
            }
            description={
              isLoginAndAddressRequired && !isLoggedIn ? (
                <FormHelp>
                  <span>
                    Please{' '}
                    <Anchor href={signInUrl}>
                      <strong>sign in</strong>
                    </Anchor>{' '}
                    to submit a request about your listing. If you are unable to sign in, select <b>My Account</b>.
                  </span>
                </FormHelp>
              ) : null
            }
            required={true}
          />
          {(selectedHelpCategory?.key as string) === '' &&
            helpTopics?.outages?.map((outage, i) => {
              return (
                <Box
                  css={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: { base: 'flex-start', lg: 'center' },
                    justifyContent: 'flex-start',
                    paddingBlock: '200',
                  }}
                  key={`outages-content-${i}`}
                >
                  <Box css={{ marginRight: '300' }}>
                    <Icon size="xl" css={{ color: 'icon.accent.blue.hero' }} render={<IconHammerFilled />} />
                  </Box>
                  <PartialMessage partialMessages={outage} fontType="body-xs" />
                </Box>
              );
            })}
          {(selectedHelpCategory?.key as string) !== '' && (
            <>
              <FormField
                label={
                  <Label>{isTopicReadOnly ? 'Topic' : 'Select the topic below that best fits your problem'}</Label>
                }
                control={
                  isTopicReadOnly ? (
                    <Input value={selectedHelpTopic?.description} readOnly={true} />
                  ) : (
                    <Select
                      aria-label="RequestType"
                      name="requestType"
                      value={selectedHelpTopic?.key}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => handleSelectTopic(e.target.value)}
                      css={{ whiteSpace: 'pre' }}
                    >
                      <option disabled={(selectedHelpTopic?.key as string) !== ''} aria-label="Please select an option">
                        -
                      </option>

                      {selectedHelpCategory?.topics?.map((topic: GuestSupportGetHelpTopicsCategoryTopicWebDto) => {
                        return (
                          <option key={topic?.key} value={topic?.key} aria-label={topic?.description}>
                            {topic?.description}
                          </option>
                        );
                      })}
                    </Select>
                  )
                }
                required={true}
              />
              <Box css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' } }}>
                <FormField
                  css={{ flex: '1 1 auto', marginRight: { base: 0, lg: '400' } }}
                  label={<Label>First name</Label>}
                  control={
                    <Input
                      value={firstName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                      className="fs-mask"
                    />
                  }
                />
                <FormField
                  css={{ flex: '1 1 auto', marginBottom: '400' }}
                  label={<Label>Last name</Label>}
                  control={
                    <Input
                      value={lastName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                      className="fs-mask"
                    />
                  }
                />
              </Box>
              <Box css={{ marginBottom: isEmailError ? '0' : '400', paddingBottom: isEmailError ? '0' : '100' }}>
                <FormField
                  label={<Label>Email address</Label>}
                  error={isEmailError}
                  description={isEmailError ? <FormHelp>Enter one valid email address</FormHelp> : null}
                  control={
                    <Input
                      value={email}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => verifyAndUpdateEmail(e.target.value)}
                    />
                  }
                  required={!isLoggedIn}
                />
              </Box>
              <Box css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' } }}>
                <Box
                  css={{
                    marginBottom: addressVerificationState.error ? '0' : '400',
                    paddingBottom: addressVerificationState.error ? '0' : '100',
                    flexBasis: '70%',
                  }}
                >
                  <FormField required={isLoginAndAddressRequired} css={{ marginRight: { base: 0, lg: 'sm' } }}>
                    <Label id="street-address-label">Listing address</Label>
                    <Combobox
                      appearance="input"
                      focusFirstOption={true}
                      options={
                        queryData?.data?.addresses?.map((address: { name: string }) => ({
                          ...address,
                          value: address.name,
                          label: address.name,
                        })) ?? []
                      }
                      onClear={() => handleAddressChange({ target: { value: '' } } as ChangeEvent<HTMLInputElement>)}
                      onOptionSelect={handleSelectAddress}
                      selectCallbackFrequency="always"
                      error={!!addressVerificationState.error}
                      disabled={Boolean(queryListingAddress)}
                      renderInput={() => {
                        return (
                          <Combobox.Input
                            ref={addressInputRef}
                            value={listingAddress}
                            onChange={handleAddressChange}
                            onBlur={() => verifyAddress(listingAddress)}
                            aria-labelledby="street-address-label"
                          />
                        );
                      }}
                      renderEmptyState={() => listingAddress.length <= 7 && null}
                      freeForm={true}
                      autoCompleteBehavior="manual"
                    />
                    {addressVerificationState.error && (
                      <FormHelp error={Boolean(addressVerificationState.error)}>
                        Enter a complete, valid address
                      </FormHelp>
                    )}
                  </FormField>
                </Box>
                <Box css={{ marginBottom: '400', flexBasis: '30%' }}>
                  <FormField
                    css={{ flex: '1 1 auto' }}
                    error={isUnitOverLimit}
                    label={<Label id="unit-number-label">Unit number</Label>}
                    control={
                      <AdornedInput
                        startAdornment={<AdornedInput.Adornment>#</AdornedInput.Adornment>}
                        input={
                          <AdornedInput.Input
                            aria-labelledby="unit-number-label"
                            // Keyed off the prefill, not the current value, so correcting a bad
                            // prefill doesn't re-lock the field mid-edit.
                            readOnly={Boolean(queryUnit) && !isUnitNumberOverLimit(queryUnit)}
                            onFocus={() => setIsUnitFocused(true)}
                            onBlur={() => setIsUnitFocused(false)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setUnit(e.target.value)}
                            value={unit || ''}
                          />
                        }
                      />
                    }
                    description={
                      <CharacterCountFormHelp
                        currentLength={(unit || '').length}
                        maxLength={AddressConstants.UNIT_NUMBER_LENGTH_LIMIT}
                        isFocused={isUnitFocused}
                        label="Unit number"
                      />
                    }
                  />
                </Box>
              </Box>
              <FormField
                label={<Label>Description</Label>}
                description={
                  selectedHelpTopic?.descriptionFieldPlaceholder ? (
                    <FormHelp>{selectedHelpTopic.descriptionFieldPlaceholder}</FormHelp>
                  ) : null
                }
                control={
                  <Textarea
                    value={description}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                  />
                }
                required={true}
              />
              <FormActions
                css={{
                  marginLeft: { base: '-16px', md: '-24px', lg: '0' },
                  lgDown: {
                    borderTop: '1px solid {colors.border.subtle}',
                    borderBottom: '1px solid {colors.border.subtle}',
                    position: 'fixed',
                    width: '100%',
                    bottom: 0,
                    padding: '400',
                    background: 'white',
                    zIndex: '2',
                  },
                }}
              >
                <Box css={{ display: 'flex', justifyContent: { base: 'flex-end', lg: 'flex-start' } }}>
                  <Button disabled={!canSubmit} tone="brand" emphasis="filled" onClick={handleSubmitRequest}>
                    Submit
                  </Button>
                </Box>
              </FormActions>
            </>
          )}
        </LoadingMask>
      </div>
    </Form>
  );
};

export default SubmitRequestForm;
